const EVENT_CAROUSEL_SLIDE_VIEWED = 'carousel_slide_viewed';

class Carousel extends HTMLElement {
  constructor() {
    super();

    // seenSlides must live on the instance (not connectedCallback) so it survives a
    // disconnect → reconnect cycle. Replo re-parents this subtree during hydration, which
    // fires the lifecycle callbacks again; keeping the set here preserves the "once per
    // unique slide per page view" analytics contract and prevents double-counting.
    this.seenSlides = new Set();

    this.slidesPerView = parseInt(this.getAttribute('data-slides-per-view')) || 1;
    this.spaceBetween = parseInt(this.getAttribute('data-space-between')) || 15;
    this.swipeThreshold = 2.5;
    this.desktopScreenSize = 992;
    this.isVerticalScroll = this.hasAttribute('vertical') && window.innerWidth >= this.desktopScreenSize;
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;
    this.isHorizontalSwipe = true;
  }

  connectedCallback() {
    // Discover DOM + bind events on every connect, not once in the constructor. When Replo
    // re-parents the carousel it disconnects then reconnects the element; re-running this
    // re-binds the swipe handlers that disconnectedCallback tore down (previously they were
    // only bound in the constructor and never restored, which killed swipe on paid landers).
    this.track = this.querySelector('.carousel-track');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.nextButton = this.querySelector('.carousel-next');
    this.prevButton = this.querySelector('.carousel-prev');
    this.thumbnails = this.getElementsByClassName('thumbnail');
    this.thumbnailContainer = this.querySelector('.carousel-thumbnails');
    this.progressBarFill = this.querySelector('.carousel-progress-bar-fill');

    this.track.style.gap = `${this.spaceBetween}px`;
    this.attachEvents();

    // Slide analytics is opted in by the product section wrapper. Other carousels reusing
    // this component (offer pages, testimonials) must not emit events.
    const productSection = this.closest('[data-product-picker-section]');
    this.isTrackingEnabled = productSection !== null;
    this.productHandle = productSection?.dataset.productHandle || '';
    this.productName = productSection?.dataset.productName || '';

    this.updateSlideWidth();
    this.updateActiveThumbnail();
    this.updateNavButtons();
    this.updateProgressBar();

    if (this.thumbnailContainer) {
      this.updateThumbnailHeight();
    }

    this.trackActiveSlide('init');
  }

  disconnectedCallback() {
    // Aborting the controller removes every listener bound in attachEvents in one shot —
    // symmetric with connectedCallback so a re-parent restores them cleanly on reconnect.
    this.eventController?.abort();
    cancelAnimationFrame(this.animationID);
    this.isDragging = false;
  }

  attachEvents = () => {
    // A single per-connect AbortController owns every listener below (track-level AND
    // document-level). disconnectedCallback aborts it, so nothing leaks and nothing is
    // double-bound when the element reconnects.
    this.eventController?.abort();
    this.eventController = new AbortController();
    const { signal } = this.eventController;

    this.boundResizeHandler = this.debounce(this.handleResize.bind(this), 200);
    window.addEventListener('resize', this.boundResizeHandler, { signal });

    this.track.addEventListener('touchstart', this.touchStart, { passive: false, signal });
    this.track.addEventListener('mousedown', this.touchStart, { signal });

    document.addEventListener('touchend', this.touchEnd, { passive: false, signal });
    document.addEventListener('mouseup', this.touchEnd, { signal });

    document.addEventListener('touchmove', this.touchMove, { passive: false, signal });
    document.addEventListener('mousemove', this.touchMove, { signal });

    this.nextButton.addEventListener('click', () => this.moveToSlide(this.currentIndex + this.slidesPerView), {
      signal,
    });
    this.prevButton.addEventListener('click', () => this.moveToSlide(this.currentIndex - this.slidesPerView), {
      signal,
    });

    Array.from(this.thumbnails).forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.moveToSlide(index), { signal });
      thumb.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.moveToSlide(index);
          }
        },
        { signal }
      );
    });

    if (this.thumbnailContainer) {
      this.attachThumbnailDragEvents(signal);
    }
  };

  debounce(func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  handleResize() {
    this.updateSlideWidth();

    if (this.thumbnailContainer) {
      this.isVerticalScroll = this.hasAttribute('vertical') && window.innerWidth >= this.desktopScreenSize;
      this.updateThumbnailHeight();
    }
  }

  attachThumbnailDragEvents(signal) {
    let isDragging = false;
    let startPos = 0;
    let scrollPosition = 0;
    let isVerticalSwipe = false;
    let touchStartX = 0;
    let touchStartY = 0;

    const startDrag = (pos) => {
      isDragging = true;
      isVerticalSwipe = false;
      startPos = pos - (this.isVerticalScroll ? this.thumbnailContainer.offsetTop : this.thumbnailContainer.offsetLeft);
      scrollPosition = this.isVerticalScroll ? this.thumbnailContainer.scrollTop : this.thumbnailContainer.scrollLeft;
      this.thumbnailContainer.classList.add('is-grabbing');
    };

    const stopDrag = () => {
      isDragging = false;
      this.thumbnailContainer.classList.remove('is-grabbing');
    };

    const dragMove = (pos) => {
      if (!isDragging || isVerticalSwipe) return;
      const walk = (pos - startPos) * 1.5;

      if (this.isVerticalScroll) {
        this.thumbnailContainer.scrollTop = scrollPosition - walk;
      } else {
        this.thumbnailContainer.scrollLeft = scrollPosition - walk;
      }
    };

    const detectSwipeDirection = (startPosX, startPosY, currentPosX, currentPosY) => {
      const deltaX = Math.abs(currentPosX - startPosX);
      const deltaY = Math.abs(currentPosY - startPosY);
      isVerticalSwipe = deltaY > deltaX;
    };

    ['mousedown', 'touchstart'].forEach((event) => {
      this.thumbnailContainer.addEventListener(
        event,
        (e) => {
          const isTouchEvent = event === 'touchstart';
          const pos = this.isVerticalScroll
            ? isTouchEvent
              ? e.touches[0].pageY
              : e.pageY
            : isTouchEvent
              ? e.touches[0].pageX
              : e.pageX;

          if (isTouchEvent) {
            touchStartX = e.touches[0].pageX;
            touchStartY = e.touches[0].pageY;
          }

          startDrag(pos, isTouchEvent);
        },
        { signal }
      );
    });

    ['mouseleave', 'mouseup', 'touchend'].forEach((event) => {
      this.thumbnailContainer.addEventListener(event, stopDrag, { signal });
    });

    ['mousemove', 'touchmove'].forEach((event) => {
      this.thumbnailContainer.addEventListener(
        event,
        (e) => {
          if (!isDragging) return;

          const pos = this.isVerticalScroll ? e.pageY || e.touches[0].pageY : e.pageX || e.touches[0].pageX;

          if (event === 'touchmove') {
            const touch = e.touches[0];
            detectSwipeDirection(touchStartX, touchStartY, touch.pageX, touch.pageY);

            if (isVerticalSwipe) {
              stopDrag();
              return;
            }
          }

          e.preventDefault();
          dragMove(pos);
        },
        { signal }
      );
    });
  }

  updateSlideWidth = () => {
    const slideFixedWidths = `calc((100% / ${this.slidesPerView}) - ((${this.spaceBetween}px / ${this.slidesPerView}) * (${this.slidesPerView} - 1)))`;
    this.slides.forEach((slide) => {
      slide.style.minWidth = slideFixedWidths;
      slide.style.display = 'block';
    });

    const slideRect = this.slides[0].getBoundingClientRect();
    this.slideWidth = slideRect.width + this.spaceBetween;

    this.setPositionByIndex();
    this.updateNavButtons();
  };

  updateThumbnailHeight() {
    const trackHeight = this.track.getBoundingClientRect().height;
    this.thumbnailContainer.style.maxHeight = `${trackHeight}px`;
  }

  moveToSlide(index) {
    const maxIndex = Math.floor(this.slides.length / this.slidesPerView) * this.slidesPerView;
    const remainingSlides = this.slides.length % this.slidesPerView;

    const newIndex =
      index >= maxIndex && remainingSlides !== 0
        ? this.slides.length - this.slidesPerView
        : Math.max(0, Math.min(index, this.slides.length - this.slidesPerView));

    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.pauseAllVideos();
      this.trackActiveSlide('navigation');
    }

    this.setPositionByIndex();
    this.updateActiveThumbnail();
    this.updateNavButtons();
    this.updateProgressBar();
  }

  updateActiveThumbnail() {
    if (this.thumbnails.length === 0) return;

    Array.from(this.thumbnails).forEach((thumb, idx) => {
      thumb.classList.toggle('active', idx === this.currentIndex);
    });

    const activeThumbnail = this.thumbnails[this.currentIndex];
    const containerScrollPosition = this.isVerticalScroll
      ? activeThumbnail.offsetTop - this.thumbnailContainer.offsetTop
      : activeThumbnail.offsetLeft - this.thumbnailContainer.offsetLeft;

    const scrollOptions = {
      behavior: 'smooth',
      [this.isVerticalScroll ? 'top' : 'left']: containerScrollPosition,
    };

    this.thumbnailContainer.scrollTo(scrollOptions);
  }

  updateProgressBar() {
    if (!this.progressBarFill) {
      return;
    }

    const totalSlides = this.slides.length;
    const currentSlide = this.currentIndex + 1;
    const progressPercentage = (currentSlide / totalSlides) * 100;

    this.progressBarFill.style.width = `${progressPercentage}%`;
  }

  getIgFields() {
    // Intelligems exposes experiments via window.igData.user. It loads asynchronously,
    // so optional chaining handles "not ready yet" / "no live test" -> empty strings,
    // and the event still fires.
    // getExperiments() returns ALL experiments on the page, not just the ones the user
    // is assigned to — so we pick the first experiment where getTestGroupName(id) is
    // non-null (that's the user's assigned variant). getTestGroup() can return null even
    // when a variant name exists, so getTestGroupName is the reliable source for the variant.
    const user = window.igData?.user;
    const experiments = user?.getExperiments?.();
    if (!experiments || !experiments.length) return { testId: '', testVariant: '' };

    for (const experiment of experiments) {
      const variantName = user.getTestGroupName?.(experiment.id);
      if (variantName) {
        return { testId: String(experiment.id), testVariant: String(variantName) };
      }
    }

    return { testId: '', testVariant: '' };
  }

  trackActiveSlide(source) {
    if (!this.isTrackingEnabled) return;

    // Only the visible instance emits — hidden duplicate has display:none → offsetParent === null.
    if (this.offsetParent === null) return;

    // Once per unique slide per page view.
    if (this.seenSlides.has(this.currentIndex)) return;
    this.seenSlides.add(this.currentIndex);

    const slideIndex = this.currentIndex + 1;
    const totalSlides = this.slides.length;
    const slideId = this.slides[this.currentIndex]?.dataset.slideId || '';

    if (window.Shopify?.analytics?.publish) {
      const ig = this.getIgFields();
      window.Shopify.analytics.publish(EVENT_CAROUSEL_SLIDE_VIEWED, {
        product_handle: this.productHandle,
        product_name: this.productName,
        slide_index: slideIndex,
        total_slides: totalSlides,
        slide_id: slideId,
        carousel_id: this.dataset.carouselId || '',
        navigation_source: source,
        ig_test_id: ig.testId,
        ig_test_variant: ig.testVariant,
      });
    }
  }

  pauseAllVideos() {
    const videos = this.querySelectorAll('video');
    videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  setPositionByIndex() {
    this.currentTranslate = this.currentIndex * -this.slideWidth;
    this.prevTranslate = this.currentTranslate;
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  updateNavButtons = () => {
    const maxIndex = Math.floor(this.slides.length / this.slidesPerView) * this.slidesPerView;
    const remainingSlides = this.slides.length % this.slidesPerView;
    const lastValidIndex =
      remainingSlides === 0 ? maxIndex - this.slidesPerView : this.slides.length - this.slidesPerView;

    this.prevButton.style.display = this.currentIndex > 0 ? 'flex' : 'none';
    this.nextButton.style.display = this.currentIndex >= lastValidIndex ? 'none' : 'flex';
  };

  touchStart = (event) => {
    if (!event.target.closest('.carousel-track')) return;

    // Re-measure the slide on drag start. Heavy Replo landers can finish loading / reflow
    // after connectedCallback measured, leaving a stale slideWidth — which throws off the
    // swipe threshold and snap distance and can make a swipe need two tries. Cheap (one read
    // per gesture) and self-correcting.
    const measuredSlide = this.slides[0]?.getBoundingClientRect();
    if (measuredSlide?.width) {
      this.slideWidth = measuredSlide.width + this.spaceBetween;
    }

    const position = this.getPosition(event);
    this.startX = position.x;
    this.startY = position.y;
    this.isHorizontalSwipe = true;

    this.isDragging = true;
    this.track.style.transition = 'none';
    this.track.style.cursor = 'grabbing';
    this.animationID = requestAnimationFrame(this.animateSlide.bind(this));
  };

  touchEnd = () => {
    if (!this.isDragging) return;
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);

    const movedBy = this.currentTranslate - this.prevTranslate;
    const threshold = this.slideWidth / this.swipeThreshold;
    const previousIndex = this.currentIndex;

    if (Math.abs(movedBy) > threshold || (this.isIOS && Math.abs(movedBy) > threshold * 0.6)) {
      this.currentIndex =
        movedBy < 0
          ? Math.min(this.currentIndex + this.slidesPerView, this.slides.length - this.slidesPerView)
          : Math.max(this.currentIndex - this.slidesPerView, 0);
    }

    this.constrainIndexWithinBounds();

    if (this.currentIndex !== previousIndex) {
      this.trackActiveSlide('swipe');
      this.pauseAllVideos();
    }

    this.setPositionByIndex();
    this.track.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    this.track.style.cursor = 'grab';

    this.updateActiveThumbnail();
    this.updateNavButtons();
    this.updateProgressBar();
  };

  constrainIndexWithinBounds() {
    const maxIndex = Math.floor(this.slides.length / this.slidesPerView) * this.slidesPerView;
    const remainingSlides = this.slides.length % this.slidesPerView;

    this.currentIndex = Math.max(
      0,
      Math.min(
        this.currentIndex,
        remainingSlides === 0 ? maxIndex - this.slidesPerView : this.slides.length - this.slidesPerView
      )
    );
  }

  touchMove = (event) => {
    if (!this.isDragging) return;

    const position = this.getPosition(event);
    const deltaX = Math.abs(position.x - this.startX);
    const deltaY = Math.abs(position.y - this.startY);

    if (deltaX < 10 && deltaY < 10) {
      return;
    }

    if (this.isHorizontalSwipe === true && deltaY > deltaX && deltaY > 15) {
      this.isHorizontalSwipe = false;
      this.isDragging = false;
      cancelAnimationFrame(this.animationID);
      this.currentTranslate = this.prevTranslate;
      this.track.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      this.track.style.cursor = 'grab';
      return;
    }

    if (this.isHorizontalSwipe && event.type === 'touchmove') {
      event.preventDefault();
    }

    if (this.isHorizontalSwipe) {
      const currentPosition = position.x;
      this.currentTranslate = this.prevTranslate + currentPosition - this.startX;
    }
  };

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  getPosition(event) {
    if (event.type.includes('mouse')) {
      return { x: event.pageX, y: event.pageY };
    } else {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
  }

  animateSlide() {
    if (!this.isDragging) return;
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    requestAnimationFrame(this.animateSlide.bind(this));
  }
}

if (!customElements.get('carousel-container')) {
  customElements.define('carousel-container', Carousel);
}
